import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

export type ScoutPopoverSession = {
  conversationId: string | undefined;
  setConversationId: (id: string | undefined) => void;
  openedAt: Date;
  /**
   * Clear the active conversation and stamp a fresh `openedAt`. Called when
   * the user explicitly dismisses the popover (X button) so the next open
   * starts a brand-new thread. Minimize must NOT call this — the whole point
   * of minimizing is to keep the conversation around.
   */
  reset: () => void;
};

/**
 * Shared session state for in-context Scout conversations.
 *
 * Held outside the popover so the popover can unmount (e.g. when the user
 * minimizes to the bottom-of-screen bar) without losing the active thread.
 * Persists across route navigation within a tenant — the conversation stays
 * put while the user moves around the app (and especially while a turn is
 * mid-flight from the minimized bar). Resets when the tenant segment of the
 * route changes so a staff/partner user switching orgs doesn't see a stale
 * thread from the previous tenant. `openedAt` captures when the chat
 * started; per-message `pageContext` keeps Scout aware of where the user is
 * right now.
 */
export function useScoutPopoverSession(): ScoutPopoverSession {
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [openedAt, setOpenedAt] = useState<Date>(() => new Date());

  const setConversationIdStable = useCallback((id: string | undefined) => {
    setConversationId(id);
  }, []);

  const reset = useCallback(() => {
    setConversationId(undefined);
    setOpenedAt(new Date());
  }, []);

  // Reset on tenant switch (staff/partner SPA navigation between orgs) so a
  // minimized thread from tenant A doesn't leak into tenant B's view. Set
  // during render — React's recommended pattern for "reset state when an
  // external value changes."
  const { tenantId } = useParams();
  const [lastTenantId, setLastTenantId] = useState(tenantId);
  if (lastTenantId !== tenantId) {
    setLastTenantId(tenantId);
    setConversationId(undefined);
    setOpenedAt(new Date());
  }

  return {
    conversationId,
    setConversationId: setConversationIdStable,
    openedAt,
    reset,
  };
}
